/**
 * Formatting commands for the rich text editor's contentEditable surface.
 *
 * document.execCommand is deprecated-but-frozen and still the only engine
 * that handles partial selections, toggling off, text node splitting, and the
 * native undo stack. Its inconsistent output (strong vs b, css spans, font
 * elements) is irrelevant here because every commit passes through
 * editorDomToCanonical, which normalizes to the canonical stored format.
 */

export type InlineFormatCommand = "bold" | "italic" | "underline" | "strikeThrough";

/**
 * Formats currently supported by the game player. Underline, strikethrough
 * and color are fully implemented in the editor but disabled until the player
 * supports them — flip the flags to re-enable, nothing else needs to change.
 */
export const ENABLED_INLINE_FORMATS: Record<InlineFormatCommand, boolean> = {
  bold: true,
  italic: true,
  underline: false,
  strikeThrough: false,
};

export const COLOR_FORMATTING_ENABLED = false;

// execCommand has no "remove color from selection" primitive, so resets
// apply a sentinel value first and then strip it from the DOM.
const SENTINEL_COLOR_HEX = "#010203";
const SENTINEL_COLOR_RGB = "rgb(1, 2, 3)";

function unwrapElement(element: Element) {
  const parent = element.parentNode;
  if (!parent) return;
  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
}

function unwrapIfPlain(element: Element) {
  if ((element.tagName === "SPAN" || element.tagName === "FONT") && element.attributes.length === 0) {
    unwrapElement(element);
  }
}

export function applyInlineFormat(command: InlineFormatCommand) {
  document.execCommand?.("styleWithCSS", false, "false");
  document.execCommand?.(command, false);
}

export function applyForeColor(editor: HTMLElement, colorHex: string | null) {
  if (colorHex) {
    document.execCommand?.("styleWithCSS", false, "true");
    document.execCommand?.("foreColor", false, colorHex);
    return;
  }

  document.execCommand?.("styleWithCSS", false, "true");
  document.execCommand?.("foreColor", false, SENTINEL_COLOR_HEX);

  editor.querySelectorAll<HTMLElement>("[style], font[color]").forEach((element) => {
    const inlineColor = element.style?.color?.toLowerCase();
    if (inlineColor === SENTINEL_COLOR_RGB || inlineColor === SENTINEL_COLOR_HEX) {
      element.style.removeProperty("color");
      if (!element.getAttribute("style")) {
        element.removeAttribute("style");
      }
    }

    if (
      element.tagName === "FONT" &&
      element.getAttribute("color")?.toLowerCase() === SENTINEL_COLOR_HEX
    ) {
      element.removeAttribute("color");
    }

    unwrapIfPlain(element);
  });
}

export interface ActiveInlineFormats {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
}

function queryCommandState(command: string): boolean {
  try {
    return document.queryCommandState?.(command) ?? false;
  } catch {
    return false;
  }
}

/**
 * queryCommandState is unreliable for some commands (notably strikeThrough),
 * so the result is OR-ed with an ancestor tag check from the selection start.
 */
export function getActiveInlineFormats(editor: HTMLElement, range: Range): ActiveInlineFormats {
  const formats: ActiveInlineFormats = {
    bold: queryCommandState("bold"),
    italic: queryCommandState("italic"),
    underline: queryCommandState("underline"),
    strike: queryCommandState("strikeThrough"),
  };

  let node: Node | null = range.startContainer;
  while (node && node !== editor) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = (node as HTMLElement).tagName;
      if (tag === "B" || tag === "STRONG") formats.bold = true;
      if (tag === "I" || tag === "EM") formats.italic = true;
      if (tag === "U") formats.underline = true;
      if (tag === "S" || tag === "STRIKE" || tag === "DEL") formats.strike = true;
    }
    node = node.parentNode;
  }

  return formats;
}
